import { useState, useRef, useEffect, useMemo } from 'react';
import { MessageCircleQuestion, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHelpChat } from '@/hooks/useHelpChat';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';

export const HelpAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userInput, setUserInput] = useState('');
  const isMobile = useIsMobile();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { messages, isLoading, isTyping, sendMessage } = useHelpChat();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!userInput.trim() || isLoading) return;
    
    const message = userInput;
    setUserInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const chatContent = useMemo(() => (
    <div className="flex flex-col h-full">
      {/* Área de Mensagens */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={cn(
                "flex gap-3 animate-fade-in",
                message.role === 'user' && "flex-row-reverse"
              )}
            >
              {message.role === 'assistant' && (
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src="/logo/logo-icon.png" alt="Disciplinado" />
                  <AvatarFallback>D</AvatarFallback>
                </Avatar>
              )}
              
              <div
                className={cn(
                  "rounded-lg px-4 py-3 max-w-[80%] break-words text-sm whitespace-pre-wrap leading-relaxed",
                  message.role === 'user'
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                )}
              >
                <ReactMarkdown 
                  components={{
                    p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                    li: ({children}) => <li className="mb-1">{children}</li>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex gap-3 animate-fade-in">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src="/logo/logo-icon.png" alt="Disciplinado" />
                <AvatarFallback>D</AvatarFallback>
              </Avatar>
              <div className="bg-muted rounded-lg px-4 py-3">
                <p className="text-sm text-muted-foreground italic">
                  Disciplinado está digitando...
                </p>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Fixo */}
      <div className="flex-shrink-0 border-t p-4 bg-background">
        <div className="flex gap-2">
          <Textarea
            placeholder="Digite sua mensagem..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            className="min-h-[60px] max-h-[120px] resize-none"
            aria-label="Mensagem para o assistente"
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!userInput.trim() || isLoading}
            className="shrink-0"
            aria-label="Enviar mensagem"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Pressione Enter para enviar, Shift+Enter para quebra de linha
        </p>
      </div>
    </div>
  ), [messages, isTyping, userInput, isLoading, handleSend, handleKeyDown]);

  const headerContent = useMemo(() => (
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        <AvatarImage src="/logo/logo-icon.png" alt="Disciplinado" />
        <AvatarFallback>D</AvatarFallback>
      </Avatar>
      <div>
        <h3 className="font-semibold">Disciplinado</h3>
        <p className="text-xs text-muted-foreground">Assistente Virtual</p>
      </div>
    </div>
  ), []);

  return (
    <>
      {/* Floating Button */}
      <Button
        size="icon"
        className={cn(
          "fixed z-50 rounded-full shadow-2xl transition-all hover:scale-105 h-14 w-14",
          isMobile ? "bottom-24 right-4" : "bottom-6 right-6"
        )}
        onClick={() => setIsOpen(true)}
        aria-label="Abrir assistente de ajuda"
      >
        <MessageCircleQuestion className="h-6 w-6" />
      </Button>

      {/* Mobile: Sheet */}
      {isMobile ? (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent 
            side="bottom" 
            className="h-[85vh] flex flex-col p-0 gap-0"
          >
            <SheetHeader className="p-4 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <SheetTitle className="flex items-center gap-3">
                  {headerContent}
                </SheetTitle>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-hidden">
              {chatContent}
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        /* Desktop: Dialog */
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden">
            <DialogHeader className="p-4 border-b flex-shrink-0">
              <DialogTitle>
                {headerContent}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              {chatContent}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
